import { useEffect, useMemo, useState } from 'react'

import { MediaQueries } from '@hhgtech/hhg-components'
import { Button, Tabs, Text } from '@hhgtech/hhg-components/mantine'
import { Box, Flex, ScrollArea, Stack, StackProps } from '@mantine/core'
import { useScrollIntoView } from '@mantine/hooks'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { useIsMobile } from 'hooks/useIsMobile'
import { fetchRelatedPosts, useExpertStore } from 'state/expert'

import { _GridArticle } from './_gridArticles'
import { StyledBorderHeading } from './index.styled'
type _RelatedPostsType = StackProps
export const _RelatedPosts = ({ ...rest }: _RelatedPostsType) => {
  const { formatMessage: f } = useIntl()
  const [activeTab, setActiveTab] = useState<string | null>('0')
  const [expertArticlePage, setExpertArticlePage] = useState(0)
  const [expertMedicalReviewedPage, setExpertMedicalReviewedPage] = useState(0)
  const isMobile = useIsMobile()
  const [isLoading, setIsLoading] = useState(false)
  const { scrollIntoView, targetRef } = useScrollIntoView<HTMLDivElement>({
    duration: 0,
    offset: 60,
    easing: (x) => 1 - (1 - x) * (1 - x),
  })
  const {
    currentExpert,
    currentExpertMedicalReviewed = [],
    currentExpertRelativePosts = [],
    currentExpertMedicalReviewedPagination,
    currentExpertRelativePostsPagination,
    setCurrentExpertMedicalReviewedPagination,
    setCurrentExpertRelativePostsPagination,
    setCurrentExpertMedicalReviewed,
    setCurrentExpertRelativePosts,
  } = useExpertStore((state) => state, shallow)
  useEffect(() => {
    ;(async () => {
      if (expertArticlePage < 1) return
      setIsLoading(true)

      const { articles, pagination } = await fetchRelatedPosts({
        id: currentExpert?.id || '',
        type: 0,
        page: expertArticlePage,
        numberOfPost: 4,
        offset: 7,
      })

      setCurrentExpertRelativePosts(articles)
      setCurrentExpertRelativePostsPagination(pagination)
      setIsLoading(false)
    })()
  }, [expertArticlePage])
  useEffect(() => {
    ;(async () => {
      if (expertMedicalReviewedPage < 1) return
      setIsLoading(true)
      const { articles, pagination } = await fetchRelatedPosts({
        id: currentExpert?.id || '',
        type: 1,
        page: expertMedicalReviewedPage,
        numberOfPost: 4,
        offset: 7,
      })

      setCurrentExpertMedicalReviewed(articles)
      setCurrentExpertMedicalReviewedPagination(pagination)
      setIsLoading(false)
    })()
  }, [expertMedicalReviewedPage])

  const hideLoadMoreExpertTab = useMemo(() => {
    return Boolean(
      Number(currentExpertRelativePostsPagination?.current_page) >=
        Number(currentExpertRelativePostsPagination?.max_num_pages),
    )
  }, [currentExpertRelativePostsPagination])

  const hideLoadMoreMedicalTab = useMemo(() => {
    return Boolean(
      (currentExpertMedicalReviewedPagination?.current_page || 0) >=
        (currentExpertMedicalReviewedPagination?.max_num_pages || 0),
    )
  }, [
    currentExpertMedicalReviewedPagination?.current_page,
    currentExpertMedicalReviewedPagination?.max_num_pages,
  ])

  const options = [
    ...(currentExpertMedicalReviewed.length > 0
      ? [
          {
            id: '0',
            name: f({
              id: 'expert.tab.medical',
              defaultMessage: 'Medically reviewed by expert',
            }),
          },
        ]
      : []),
    ...(currentExpertRelativePosts.length > 0
      ? [
          {
            id: '1',
            name: f({
              id: 'expert.tab.expert',
              defaultMessage: 'Expert’s article',
            }),
          },
        ]
      : []),
    ...[],
  ]

  const handleActiveTab = (activeTab: string) => {
    scrollIntoView({
      alignment: 'start',
    })
    setActiveTab(activeTab)
  }
  return currentExpertRelativePosts.length > 0 ||
    currentExpertMedicalReviewed.length > 0 ? (
    <Stack {...rest}>
      <Box maw={400} pos="relative">
        {/* ...other content */}
      </Box>
      <div ref={targetRef}>
        <StyledBorderHeading tag="h4">
          {f({
            id: 'expert.content.latestArticles',
            defaultMessage: 'Latest Expert Articles',
          })}
        </StyledBorderHeading>
      </div>
      {/* TODO: Create layout of this */}
      <Tabs
        defaultValue={'0'}
        value={activeTab}
        onTabChange={handleActiveTab}
        pos="relative"
      >
        <ScrollArea
          offsetScrollbars
          scrollbarSize={4}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#ffffff',
          }}
        >
          <Flex justify={'space-between'}>
            <Tabs.List
              grow
              sx={{
                [MediaQueries.mbDown]: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: '#ffffff',
                },
              }}
            >
              {options.map((opt) => (
                <Tabs.Tab key={opt.id} value={opt.id}>
                  <Text size="s3" weight="semiBold" color="inherit">
                    {opt.name}
                  </Text>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Flex>
        </ScrollArea>
        {currentExpertRelativePosts.length > 0 && (
          <Tabs.Panel value="1" pos="relative" pt={{ base: 16, md: 24 }}>
            <Stack justify="center" align="center">
              <_GridArticle articles={currentExpertRelativePosts} />
              {!hideLoadMoreExpertTab && (
                <Button
                  mt={{ base: 0, md: 24 }}
                  loading={isLoading}
                  variant="tertiary"
                  w={!isMobile ? 'fit-content' : '100%'}
                  fullWidth={isMobile}
                  onClick={() => setExpertArticlePage((pre) => ++pre)}
                >
                  {f({
                    id: 'expert.loadMore',
                    defaultMessage: 'Load more',
                  })}
                </Button>
              )}
            </Stack>
          </Tabs.Panel>
        )}
        {currentExpertMedicalReviewed.length > 0 && (
          <Tabs.Panel value="0" pos="relative" pt={{ base: 16, md: 24 }}>
            <Stack justify="center" align="center">
              <_GridArticle articles={currentExpertMedicalReviewed} />
              {!hideLoadMoreMedicalTab && (
                <Button
                  mt={{ base: 0, md: 24 }}
                  loading={isLoading}
                  variant="tertiary"
                  w={!isMobile ? 'fit-content' : '100%'}
                  fullWidth={isMobile}
                  onClick={() => setExpertMedicalReviewedPage((pre) => ++pre)}
                >
                  {f({
                    id: 'expert.loadMore',
                    defaultMessage: 'Load more',
                  })}
                </Button>
              )}
            </Stack>
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  ) : null
}
